import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  CalendarCheck,
  Search,
  Filter,
  Check,
  X,
  Clock,
  RefreshCw,
  CheckCircle,
  Trash2,
  Eye,
  Phone,
  Mail,
  Calendar,
  User,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminBookings = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const fetchBookings = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/api/admin/bookings`
        : `${API}/api/admin/bookings?status=${filter}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [token, filter]);

  const updateBookingStatus = async (bookingId, newStatus, notes = '') => {
    try {
      const response = await fetch(`${API}/api/admin/bookings/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          booking_status: newStatus,
          admin_notes: notes || undefined
        })
      });

      if (response.ok) {
        toast.success(`Booking ${newStatus} successfully`);
        fetchBookings();
        setShowModal(false);
        setSelectedBooking(null);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to delete this booking?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Booking deleted');
        fetchBookings();
      }
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      rescheduled: 'bg-blue-100 text-blue-700',
      completed: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const filteredBookings = bookings.filter(booking =>
    booking.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.phone?.includes(searchTerm)
  );

  const statusCounts = {
    all: bookings.length,
    pending: bookings.filter(b => b.booking_status === 'pending').length,
    approved: bookings.filter(b => b.booking_status === 'approved').length,
    rejected: bookings.filter(b => b.booking_status === 'rejected').length,
    completed: bookings.filter(b => b.booking_status === 'completed').length,
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-primary" />
              Bookings Management
            </h1>
            <p className="text-slate-600">Manage appointment bookings and schedules</p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
            </button>
          ))}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading bookings...</div>
            ) : filteredBookings.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No bookings found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Patient</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Contact</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Doctor/Dept</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Date & Time</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <span className="font-medium text-slate-800">{booking.patient_name}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {booking.phone}
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {booking.email}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {booking.doctor_name || booking.department_name || '-'}
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-1 text-slate-800">
                              <Calendar className="w-3 h-3" />
                              {booking.preferred_date}
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Clock className="w-3 h-3" />
                              {booking.preferred_time}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(booking.booking_status)}`}>
                            {booking.booking_status}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setAdminNotes(booking.admin_notes || '');
                                setShowModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {booking.booking_status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600"
                                  onClick={() => updateBookingStatus(booking.id, 'approved')}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-red-500 hover:bg-red-600"
                                  onClick={() => updateBookingStatus(booking.id, 'rejected')}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 hover:bg-red-50"
                              onClick={() => deleteBooking(booking.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Booking Detail Modal */}
        {showModal && selectedBooking && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Booking Details</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Patient Name</Label>
                    <p className="font-medium">{selectedBooking.patient_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Status</Label>
                    <p><span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedBooking.booking_status)}`}>
                      {selectedBooking.booking_status}
                    </span></p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Phone</Label>
                    <p>{selectedBooking.phone}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email</Label>
                    <p>{selectedBooking.email}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Date</Label>
                    <p>{selectedBooking.preferred_date}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Time</Label>
                    <p>{selectedBooking.preferred_time}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500">Doctor / Department</Label>
                    <p>{selectedBooking.doctor_name || selectedBooking.department_name || '-'}</p>
                  </div>
                </div>

                {selectedBooking.message && (
                  <div>
                    <Label className="text-slate-500">Patient Message</Label>
                    <p className="text-sm bg-slate-50 p-3 rounded-lg mt-1">{selectedBooking.message}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="adminNotes">Admin Notes</Label>
                  <textarea
                    id="adminNotes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="w-full mt-1 p-3 border rounded-lg text-sm"
                    rows={3}
                    placeholder="Add notes about this booking..."
                  />
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    className="bg-green-500 hover:bg-green-600"
                    onClick={() => updateBookingStatus(selectedBooking.id, 'approved', adminNotes)}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    className="bg-red-500 hover:bg-red-600"
                    onClick={() => updateBookingStatus(selectedBooking.id, 'rejected', adminNotes)}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={() => updateBookingStatus(selectedBooking.id, 'rescheduled', adminNotes)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reschedule
                  </Button>
                  <Button
                    className="bg-slate-500 hover:bg-slate-600"
                    onClick={() => updateBookingStatus(selectedBooking.id, 'completed', adminNotes)}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBookings;
